import { BlockRounded, CheckRounded, WarningRounded } from '@mui/icons-material';
import { alpha, Box, Chip, Stack, TooltipProps, Typography } from '@mui/material';
import { MRT_Row, MRT_RowSelectionState as RowSelectionState } from 'material-react-table';
import React from 'react';
import type { TextFieldProps } from '~/components/Form/TextField/TextField';
import theme from '~/theme';
import {
  SupplyManagerFilterSelectField,
  supplyManagerFilterSelectMenuProps,
  SupplyManagerGenericTooltip,
} from './supply-manager.styles';
import type {
  SupplyManagerPartnerData,
  SupplyManagerPartnerGroupData,
} from './useSupplyManagerPartnersQuery';

export const PUBLISHER_BIDDING_PATH_SUFFIX = '/publisher-bidding';

/** Strip trailing slashes so `/publisher-bidding/` still counts as the bidding route. */
export function normalizeSupplyManagerPathname(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized || '/';
}

export function isPublisherBiddingPathname(pathname: string): boolean {
  return normalizeSupplyManagerPathname(pathname).endsWith(PUBLISHER_BIDDING_PATH_SUFFIX);
}

export function getSupplyManagerBasePath(pathname: string): string {
  const normalized = normalizeSupplyManagerPathname(pathname);
  return isPublisherBiddingPathname(normalized)
    ? normalized.slice(0, -PUBLISHER_BIDDING_PATH_SUFFIX.length)
    : normalized;
}

/** Extract a comparable hostname from a bare domain or URL-like string. */
function normalizeHostname(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }

  try {
    const { hostname } = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
    return hostname.replace(/\.$/, '') || null;
  } catch {
    const fallback = trimmed.split('/')[0]?.split('?')[0]?.split(':')[0]?.replace(/\.$/, '');
    return fallback || null;
  }
}

function isDomainUnderBlockedDomain(domain: string | undefined, blockedDomain: string): boolean {
  if (!domain) {
    return false;
  }

  const domainHostname = normalizeHostname(domain);
  const blockedHostname = normalizeHostname(blockedDomain);

  if (!domainHostname || !blockedHostname) {
    return domain.toLowerCase() === blockedDomain.toLowerCase();
  }

  return domainHostname === blockedHostname || domainHostname.endsWith(`.${blockedHostname}`);
}

export type SupplyManagerTableRow = SupplyManagerPartnerGroupData | SupplyManagerPartnerData;

/** MUI Icons has no per-locale icons — flag emoji used for visual identity in selectors. */
export const SUPPLY_MANAGER_LANGUAGE_MAP = {
  EN: { label: 'English', flagEmoji: '🇬🇧' },
  DE: { label: 'German', flagEmoji: '🇩🇪' },
  IT: { label: 'Italian', flagEmoji: '🇮🇹' },
  ZH: { label: 'Chinese', flagEmoji: '🇨🇳' },
  FR: { label: 'French', flagEmoji: '🇫🇷' },
  ES: { label: 'Spanish', flagEmoji: '🇪🇸' },
  PL: { label: 'Polish', flagEmoji: '🇵🇱' },
  JA: { label: 'Japanese', flagEmoji: '🇯🇵' },
  SV: { label: 'Swedish', flagEmoji: '🇸🇪' },
  FI: { label: 'Finnish', flagEmoji: '🇫🇮' },
  NO: { label: 'Norwegian', flagEmoji: '🇳🇴' },
  IW: { label: 'Hebrew', flagEmoji: '🇮🇱' },
  HR: { label: 'Croatian', flagEmoji: '🇭🇷' },
  PT: { label: 'Portuguese', flagEmoji: '🇵🇹' },
  AR: { label: 'Arabic', flagEmoji: '🇸🇦' },
  NL: { label: 'Dutch', flagEmoji: '🇳🇱' },
  ID: { label: 'Indonesian', flagEmoji: '🇮🇩' },
  KO: { label: 'Korean', flagEmoji: '🇰🇷' },
  DA: { label: 'Danish', flagEmoji: '🇩🇰' },
  RO: { label: 'Romanian', flagEmoji: '🇷🇴' },
  VI: { label: 'Vietnamese', flagEmoji: '🇻🇳' },
  TR: { label: 'Turkish', flagEmoji: '🇹🇷' },
  BG: { label: 'Bulgarian', flagEmoji: '🇧🇬' },
  TH: { label: 'Thai', flagEmoji: '🇹🇭' },
} as const;

export type SupplyManagerLanguageCode = keyof typeof SUPPLY_MANAGER_LANGUAGE_MAP;

export function getSupplyManagerLanguageDisplay(code: string): string {
  const normalized = code.trim().toUpperCase();
  return SUPPLY_MANAGER_LANGUAGE_MAP[normalized as SupplyManagerLanguageCode]?.label ?? code;
}

export function SupplyManagerLanguageLabel({ code }: { code: string }) {
  const meta = SUPPLY_MANAGER_LANGUAGE_MAP[code.trim().toUpperCase() as SupplyManagerLanguageCode];

  if (!meta) {
    return <>{code}</>;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      component="span"
      sx={{ '& span.MuiBox-root': { m: 0 }, gap: '5px' }}
    >
      <Box component="span" aria-hidden sx={{ fontSize: 14, lineHeight: 1 }}>
        {meta.flagEmoji}
      </Box>
      <Box component="span">{meta.label}</Box>
    </Stack>
  );
}

export type SupplyManagerRowBlockingData = {
  hasBlockedSubRow: boolean;
  hasBlockedParentRow: boolean;
  hasSelectedSubRow: boolean;
  hasSelectedParentRow: boolean;
  isDisabled: boolean;
};

export function getSupplyManagerRowBlockingData(
  row: MRT_Row<SupplyManagerTableRow>,
  blockedPartnerIds: number[],
  blockedDomainGroups: string[],
  rowSelection: RowSelectionState,
): SupplyManagerRowBlockingData {
  const subRowIds = row.originalSubRows
    ?.map((subRow) => (subRow.type === 'publisher' ? subRow.id : null))
    .filter(Boolean);

  const hasBlockedSubRow = blockedPartnerIds.some(
    (id) => subRowIds?.some((subRowId) => subRowId === id),
  );

  let hasBlockedParentRow = false;
  if (row.original.type === 'publisher') {
    const { domainList } = row.original;
    hasBlockedParentRow = blockedDomainGroups.some(
      (blockedDomain) =>
        domainList?.some((domain) => isDomainUnderBlockedDomain(domain, blockedDomain)),
    );
  }

  const hasSelectedSubRow =
    !!subRowIds &&
    subRowIds.some((sr) => Object.keys(rowSelection).some((rw) => String(sr) === rw));

  const hasSelectedParentRow = !!row.parentId && !!rowSelection[row.parentId];

  const isDisabled =
    hasBlockedSubRow || hasBlockedParentRow || hasSelectedSubRow || hasSelectedParentRow;

  return {
    hasBlockedSubRow,
    hasBlockedParentRow,
    hasSelectedSubRow,
    hasSelectedParentRow,
    isDisabled,
  };
}

export type SupplyManagerRowBlockTooltipContent = {
  title: string;
  message: string;
  icon: React.ReactElement;
};

export type SupplyManagerRowBlockTooltipParams = {
  rowType: 'domain-group' | 'publisher';
  isBlocked: boolean;
  isBlockingLimitReached: boolean;
  blockLimit: number;
  blockingData: SupplyManagerRowBlockingData;
  /** When true, only show a tooltip if selection/blocking is restricted (for row checkboxes). */
  onlyShowWhenRestricted?: boolean;
};

export function getSupplyManagerRowBlockTooltipContent({
  isBlocked,
  isBlockingLimitReached,
  blockLimit,
  blockingData,
  onlyShowWhenRestricted = false,
  rowType,
}: SupplyManagerRowBlockTooltipParams): SupplyManagerRowBlockTooltipContent | null {
  const { hasBlockedSubRow, hasBlockedParentRow, hasSelectedSubRow, hasSelectedParentRow } =
    blockingData;

  const rowTypeLabel = rowType.replace('-', ' ');

  if (isBlocked) {
    return {
      title: 'Blocked',
      message: `This ${rowTypeLabel} is blocked. Campaigns will not run on this traffic.`,
      icon: <BlockRounded color="error" />,
    };
  }

  if (hasSelectedParentRow) {
    return {
      title: 'Domain group blocked',
      message:
        'This domain group is already blocked. Unblock it before blocking individual sub-publishers.',
      icon: <WarningRounded color="warning" />,
    };
  }

  if (hasSelectedSubRow) {
    return {
      title: 'Sub-publishers blocked',
      message:
        'One or more sub-publishers in this group are blocked. Unblock them before blocking the whole domain group.',
      icon: <WarningRounded color="warning" />,
    };
  }

  if (hasBlockedSubRow) {
    return {
      title: "Can't block domain group - sub-publishers blocked",
      message:
        'A sub-publisher in this group is already blocked. Unblock all sub-publishers first to avoid rule conflicts before blocking at the domain level.',
      icon: <WarningRounded color="warning" />,
    };
  }

  if (hasBlockedParentRow) {
    return {
      title: "Can't block sub-publisher - domain group blocked",
      message:
        "This publisher's domain group is already blocked. Remove the domain-level block first to avoid rule conflicts before blocking individual sub-publishers.",
      icon: <WarningRounded color="warning" />,
    };
  }

  if (isBlockingLimitReached) {
    return {
      title: 'Blocking limit reached',
      message: `You can block up to ${blockLimit} publishers. Unblock some before adding more.`,
      icon: <WarningRounded color="warning" />,
    };
  }

  if (onlyShowWhenRestricted) {
    return null;
  }

  return {
    title: 'Unblocked',
    message: `This ${rowTypeLabel} is not blocked. Campaigns can run on this traffic where applicable.`,
    icon: <CheckRounded color="success" />,
  };
}

export function SupplyManagerFilterSelect({ SelectProps, margin, size, ...props }: TextFieldProps) {
  return (
    <SupplyManagerFilterSelectField
      size={size ?? 'small'}
      margin={margin ?? 'none'}
      SelectProps={{
        ...SelectProps,
        MenuProps: {
          ...supplyManagerFilterSelectMenuProps,
          ...SelectProps?.MenuProps,
          PaperProps: {
            ...supplyManagerFilterSelectMenuProps.PaperProps,
            ...SelectProps?.MenuProps?.PaperProps,
            sx: {
              ...supplyManagerFilterSelectMenuProps.PaperProps.sx,
              ...(SelectProps?.MenuProps?.PaperProps as { sx?: object } | undefined)?.sx,
            },
          },
        },
      }}
      {...props} // eslint-disable-line react/jsx-props-no-spreading -- TextField passthrough
    />
  );
}

export function SupplyManagerBlockingLimitStatusChip({
  totalBlockCount,
  blockLimit,
}: {
  totalBlockCount: number;
  blockLimit: number;
}) {
  const atOrOverLimit = totalBlockCount >= blockLimit;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
      <Chip
        size="small"
        variant="outlined"
        label={
          atOrOverLimit
            ? `Blocking limit reached (${blockLimit}). Unblock publishers to block more.`
            : `You can block up to ${blockLimit} publishers`
        }
        icon={
          atOrOverLimit ? (
            <WarningRounded sx={{ fontSize: 14 }} />
          ) : (
            <CheckRounded sx={{ fontSize: 14 }} />
          )
        }
        sx={{
          alignSelf: 'flex-start',
          width: 'fit-content',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: 500,
          p: 0.3,
          '& .MuiChip-label': {
            px: 1,
            py: 0.375,
            whiteSpace: 'normal',
            lineHeight: 1.35,
          },
          ...(atOrOverLimit
            ? {
                borderColor: alpha(theme.palette.warning.main, 0.45),
                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                color: theme.palette.warning.dark,
                '& .MuiChip-icon': {
                  color: theme.palette.warning.main,
                },
              }
            : {
                borderColor: alpha(theme.palette.dianomiGreen.main, 0.35),
                backgroundColor: alpha(theme.palette.dianomiGreen.main, 0.06),
                color: theme.palette.text.secondary,
                '& .MuiChip-icon': {
                  color: theme.palette.dianomiGreen.main,
                },
              }),
        }}
      />
    </Box>
  );
}

export function SupplyManagerGenericTooltipContent({
  title,
  message,
  icon,
}: {
  title: string;
  message: string;
  icon: React.ReactNode;
}) {
  return (
    <Box flexDirection="column">
      <Box
        display="flex"
        height="20%"
        flexDirection="row"
        justifyContent="center"
        padding={0.5}
        sx={{
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.common.black,
          padding: 1,
        }}
      >
        <Box flex="2" display="flex" flexDirection="row" justifyContent="center">
          {icon}
        </Box>
        <Box flex="10" display="flex" flexDirection="column" justifyContent="center">
          <Typography variant="h3">{title}</Typography>
        </Box>
      </Box>
      <Box height="80%" padding={2}>
        {message}
      </Box>
    </Box>
  );
}

export function SupplyManagerRowBlockTooltip({
  children,
  onlyShowWhenRestricted = false,
  placement = 'top',
  ...params
}: SupplyManagerRowBlockTooltipParams & {
  children: React.ReactElement;
  onlyShowWhenRestricted?: boolean;
  placement?: TooltipProps['placement'];
}) {
  const content = getSupplyManagerRowBlockTooltipContent({
    ...params,
    onlyShowWhenRestricted,
  });

  return (
    <SupplyManagerGenericTooltip
      title={
        content ? (
          <SupplyManagerGenericTooltipContent
            title={content.title}
            message={content.message}
            icon={content.icon}
          />
        ) : null
      }
      placement={placement}
    >
      {children}
    </SupplyManagerGenericTooltip>
  );
}
